<template>
    <div>
        <be-card
            table
            featured
            header-contrast
            :folded="!showFilters"
            @header-click="showFilters = !showFilters"
            v-if="filterable && !loading"
        >
            <template slot="header">
                <div class="tools">
                    <span :class="`icon mdi mdi-chevron-${showFilters ? 'down' : 'up'}`" @click.stop="showFilters = !showFilters"></span>
                </div>
                <i class="icon mdi mdi-filter-list"></i>&nbsp;
                <span class="title">Digital.Solutions filtern</span>
            </template>

            <div class="row table-filters-container">
                <div class="col-12 col-lg-6 col-xl-4 table-filters pb-0">
                    <span class="table-filter-title">Stichwort</span>
                    <div class="filter-container">
                        <text-field label="Geben Sie einen Suchbegriff ein:" v-model="keywords"/>
                    </div>
                </div>
                <div class="col-12 col-lg-6 col-xl-4 table-filters pb-0">
                    <span class="table-filter-title">Klassifizierung</span>
                    <div class="filter-container">
                        <div class="row">
                            <div class="col-6">
                                <tag-list multiple label="Tags:" :options="tagOptions" v-model="tags"/>
                            </div>
                            <div class="col-6">
                                <select-list
                                    multiple
                                    label="Kategorien:"
                                    :options="categoryOptions"
                                    v-model="categories"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-lg-6 col-xl-4 table-filters pb-0">
                    <span class="table-filter-title">Sortieren nach</span>
                    <div class="filter-container">
                        <select-menu label="Wählen Sie eine Sortierung:" :options="sortOptions" v-model="sortBy"/>
                    </div>
                </div>

                <div class="col-12 col-lg-6 col-xl-4 table-filters pb-0">
                    <span class="table-filter-title">Zeitaufwand</span>
                    <div class="filter-container">
                        <range-slider
                            label="Wählen Sie einen maximalen Zeitaufwand:"
                            :min="10"
                            :max="100"
                            :step="10"
                            :formatter="formatTimeNeeded"
                            v-model="timeNeeded"
                        />
                    </div>
                </div>
                <div class="col-12 col-xl-8 table-filters pb-0">
                    <span class="table-filter-title">Kosten</span>
                    <div class="filter-container">
                        <div class="row">
                            <div class="col-12 col-lg-6">
                                <range-slider
                                    label="Einmalige Kosten:"
                                    :min="costsScale.oneTime.min"
                                    :max="costsScale.oneTime.max"
                                    :formatter="formatCosts"
                                    v-model="costsOneTime"
                                />
                            </div>
                            <div class="col-12 col-lg-6">
                                <range-slider
                                    label="Wiederkehrende Kosten:"
                                    :min="costsScale.recurring.min"
                                    :max="costsScale.recurring.max"
                                    :formatter="formatCosts"
                                    v-model="costsRecurring"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </be-card>

        <div class="row df-solutions" v-if="loading">
            <div class="col" v-for="n in 12" :key="n">
                <be-card border-color="primary" class="pt-3">
                    <content-placeholders>
                        <content-placeholders-img/>
                        <content-placeholders-text :lines="5" />
                    </content-placeholders>
                </be-card>
            </div>
        </div>

        <template v-else-if="!solutionsWithCategories.length || (filterable && !filteredSolutions.length)">
            <slot name="empty">
                <be-alert center border icon="alert-triangle" color="warning" v-if="!solutionsWithCategories.length">
                    <strong>Keine Ergebnisse</strong>
                    <p>Es wurden keine Digital.Solutions gefunden.</p>

                    <router-link
                        :to="{ name: 'create-solution' }"
                        class="btn btn-success btn-small btn-space mt-3"
                        v-if="hasRole('ROLE_SOLUTIONS')"
                    >Solution erfassen</router-link>
                </be-alert>

                <be-alert center border icon="alert-triangle" color="warning" v-else>
                    <strong>Keine Ergebnisse</strong>
                    <p>Es wurden keine Digital.Solutions gefunden die Ihren Filter-Kriterien entsprechen.</p>

                    <button class="btn btn-primary btn-small btn-space mt-3" @click="() => resetFilter()">Filter zurücksetzen</button>
                    <router-link
                        :to="{ name: 'create-solution' }"
                        class="btn btn-success btn-small btn-space mt-3"
                        v-if="hasRole('ROLE_SOLUTIONS')"
                    >Solution erfassen</router-link>
                </be-alert>
            </slot>
        </template>

        <div class="row df-solutions" v-else>
            <template v-for="(solution, key) in filteredSolutions">
                <div class="col col-12 d-none d-print-block break-before-always" v-if="key > 0 && (key % 3) === 0" :key="`separator_${key}`"></div>
                <div class="col col-print-4" :key="solution.id">
                    <keep-alive>
                        <df-solution
                            :filterable="filterable"
                            :solution="solution"
                            @filter-tag="onFilterTag"
                            @filter-category="onFilterCategory"
                            :key="`solution_${solution.id}`"
                        >
                            <template slot="tools">
                                <slot name="tools" :solution="solution"/>
                            </template>
                        </df-solution>
                    </keep-alive>
                </div>
            </template>
        </div>

    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import arrayUniq from 'array-uniq';
    import Solution from '../../models/Solution';
    import FrameworkCategory from '../../models/FrameworkCategory';
    import FrameworkCategories from '../../data/FrameworkCategories';
    import DfSolution from './DfSolution';

    export default {
        components: { DfSolution },

        props: {
            solutions: Array,
            filterable: Boolean,
            defaultCategory: FrameworkCategory,
        },

        data: vm => ({
            loading: true,
            tagOptions: [],
            categoryOptions: [],
            costsScale: {
                oneTime: { min: 0, max: 0 },
                recurring: { min: 0, max: 0 },
            },
            solutionsWithCategories: [],
            filteredSolutions: [],
            count: 0,

            showFilters: vm.$mq.md,
            keywords: '',
            timeNeeded: 100,
            costsOneTime: [0, 0],
            costsRecurring: [0, 0],
            tags: [],
            categories: vm.defaultCategory ? [vm.defaultCategory.resourceId] : [],
            sortBy: '',

            sortOptions: [
                { value: 'name_asc', label: 'Name (A-Z)' },
                { value: 'name_desc', label: 'Name (Z-A)' },
                { value: 'created', label: 'Neuste' },
                { value: 'updated', label: 'Zuletzt geändert' },
            ],
        }),

        computed: {
            ...mapGetters('user', ['hasRole']),
        },

        methods: {
            loadSolutions() {
                this.loading = true;

                if (this.solutions) {
                    this.solutionsWithCategories = Object.freeze(this.solutions.filter(s => s.hasCategories));
                    this.tagOptions = arrayUniq(this.solutionsWithCategories.reduce((v, s) => v.concat(s.tags), []));
                    this.updateCostScale();
                    this.filterSolutions();
                    this.loading = false;
                }
            },

            filterSolutions() {
                const solutions = this.solutionsWithCategories.filter((solution) => {
                    if (solution.timeNeeded > this.timeNeeded) {
                        return false;
                    }

                    if (solution.costsOneTime < this.costsOneTime[0] || solution.costsOneTime > this.costsOneTime[1]) {
                        return false;
                    }

                    if (solution.costsRecurring < this.costsRecurring[0] || solution.costsRecurring > this.costsRecurring[1]) {
                        return false;
                    }

                    return this.matchKeywords(solution) && this.matchCategories(solution) && this.matchTags(solution);
                }).sort((a, b) => {
                    switch (this.sortBy) {
                        case 'name_asc':
                        default:
                            return a.title.localeCompare(b.title);

                        case 'name_desc':
                            return b.title.localeCompare(a.title);

                        case 'created':
                            return b.createdAt.localeCompare(a.createdAt);

                        case 'updated':
                            return b.updatedAt.localeCompare(a.updatedAt);
                    }
                });

                setTimeout(() => {
                    this.filteredSolutions = Object.freeze(solutions);
                }, 50);
            },

            updateCostScale() {
                const scale = {
                    oneTime: { min: 0, max: 0 },
                    recurring: { min: 0, max: 0 },
                };

                this.solutionsWithCategories
                    .filter(s => (this.matchKeywords(s) && this.matchCategories(s) && this.matchTags(s)))
                    .forEach((solution) => {
                        const oneTime = Number(solution.costsOneTime);
                        const recurring = Number(solution.costsRecurring);

                        if (scale.oneTime.min > 0 && scale.oneTime.min > oneTime) {
                            scale.oneTime.min = oneTime;
                        }

                        if (scale.oneTime.max < oneTime) {
                            scale.oneTime.max = oneTime;
                        }

                        if (scale.recurring.min > 0 && scale.recurring.min > recurring) {
                            scale.recurring.min = recurring;
                        }

                        if (scale.recurring.max < recurring) {
                            scale.recurring.max = recurring;
                        }
                    });

                this.costsScale = scale;
                this.costsOneTime = [scale.oneTime.min, scale.oneTime.max];
                this.costsRecurring = [scale.recurring.min, scale.recurring.max];
            },

            formatTimeNeeded(value) {
                return Solution.TimeNeededMap[value];
            },

            formatCosts(values) {
                let from = values[0];
                let to = values[1];

                if (Intl && Intl.NumberFormat) {
                    from = new Intl.NumberFormat('de-CH').format(from);
                    to = new Intl.NumberFormat('de-CH').format(to);
                }

                return `${from} CHF bis ${to} CHF`;
            },

            onFilterTag(tag) {
                this.tags.push(tag);
            },

            onFilterCategory(category) {
                this.categories.push(category.resourceId);
            },

            matchKeywords(solution) {
                if (this.keywords) {
                    const search = `${solution.title.toLowerCase()} ${solution.title.toLowerCase()}`;
                    const words = this.keywords.toLowerCase().split(' ');
                    let i;

                    for (i = 0; i < words.length; i += 1) {
                        if (!search.includes(words[i])) {
                            return false;
                        }
                    }
                }

                return true;
            },

            matchCategories(solution) {
                if (this.categories.length) {
                    let i;
                    let hasCategory = false;
                    for (i = 0; i < this.categories.length; i += 1) {
                        if (solution.frameworkCategories.includes(this.categories[i])) {
                            hasCategory = true;
                        }
                    }

                    if (!hasCategory) {
                        return false;
                    }
                }

                return true;
            },

            matchTags(solution) {
                if (this.tags.length) {
                    if (!solution.tags) {
                        return false;
                    }

                    const tags = Array.from(solution.tags);
                    if (!tags) {
                        return false;
                    }

                    let i;
                    let hasTag = false;
                    for (i = 0; i < this.tags.length; i += 1) {
                        if (tags.includes(this.tags[i])) {
                            hasTag = true;
                        }
                    }

                    if (!hasTag) {
                        return false;
                    }
                }

                return true;
            },

            resetFilter(resetCategory = false) {
                this.keywords = '';
                this.timeNeeded = 100;
                this.costsOneTime = [this.costsScale.oneTime.min, this.costsScale.oneTime.max];
                this.costsRecurring = [this.costsScale.recurring.min, this.costsScale.recurring.max];
                this.tags = [];
                this.categories = (resetCategory && this.defaultCategory) ? [this.defaultCategory.resourceId] : [];
            },
        },

        watch: {
            defaultCategory() {
                this.resetFilter();
            },

            solutions() {
                this.loadSolutions();
            },

            timeNeeded() {
                this.filterSolutions();
            },

            costsOneTime() {
                this.filterSolutions();
            },

            costsRecurring() {
                this.filterSolutions();
            },

            keywords() {
                this.updateCostScale();
                this.filterSolutions();
            },

            categories() {
                this.updateCostScale();
                this.filterSolutions();
            },

            tags() {
                this.updateCostScale();
                this.filterSolutions();
            },

            sortBy() {
                this.filterSolutions();
            },
        },

        async mounted() {
            this.categoryOptions = (await FrameworkCategories.all()).map(c => ({
                value: c.resourceId,
                label: c.title,
            }));

            this.resetFilter(true);
            this.loadSolutions();
        },
    };
</script>


<style rel="stylesheet/scss" lang="scss">
    .df-solutions {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));

        @media print {
            display: block;
            overflow: hidden;

            > .col {
                float: left;
            }
        }
    }
</style>
